import {XMLParser} from 'fast-xml-parser'
import {FILIAIS_OPTIONS} from 'diferpan-common-web'
import {extractNumbers} from './string-utils'

export interface DestinatarioNotaFiscal {
  chaveNfe: string
  uf: string
}

export type EstadoConferenciaUf = 'SEM_XML' | 'VALIDADO' | 'CONFLITO'

export type ConferenciaUfNota = 'CONFERIDA' | 'RESERVADA_SEM_XML' | 'CIENCIA_UF_DIVERGENTE'

export interface NotaFiscalXml {
  chaveNfe: string | null
  ufDestinatario: string | null
  razaoSocialEmitente: string | null
  dataEmissao: string | null
  volumeCarga: string | null
}

interface VolumeXml {
  qVol?: unknown
}

interface InfNfeXml {
  '@_Id'?: unknown
  emit?: {xNome?: unknown}
  ide?: {dhEmi?: unknown}
  dest?: {enderDest?: {UF?: unknown}}
  transp?: {vol?: VolumeXml | VolumeXml[]}
}

interface DocumentoNfeXml {
  nfeProc?: {NFe?: {infNFe?: InfNfeXml}}
  NFe?: {infNFe?: InfNfeXml}
}

interface DocumentoCteXml {
  cteProc?: {CTe?: {infCte?: {ide?: {nCT?: unknown}}}}
  CTe?: {infCte?: {ide?: {nCT?: unknown}}}
}

const FILIAL_MATRIZ = '1'
const FILIAL_AGREGADA_MATRIZ = '18'

const criarParser = () => new XMLParser({ignoreAttributes: false, attributeNamePrefix: '@_'})

const textoOuNulo = (valor: unknown): string | null => {
  if (valor === undefined || valor === null) return null
  const texto = String(valor).trim()
  return texto.length > 0 ? texto : null
}

const analisarXml = <T>(xml: string): T | null => {
  try {
    return criarParser().parse(xml) as T
  } catch {
    return null
  }
}

export const buscarUfDaFilial = (filial: string) => {
  const codigoFilial = filial === FILIAL_AGREGADA_MATRIZ ? FILIAL_MATRIZ : filial
  return FILIAIS_OPTIONS.find(opcao => opcao.value === codigoFilial)?.label
}

export const buscarFilialDaUf = (uf: string | null) => FILIAIS_OPTIONS.find(opcao => opcao.label === uf)

export const classificarConferenciaUf = (ufDestinatario: string | null, ufFilial: string): EstadoConferenciaUf => {
  if (ufDestinatario === null) return 'SEM_XML'
  return ufDestinatario === ufFilial ? 'VALIDADO' : 'CONFLITO'
}

export const resolverMarcaConferenciaNota = (estado: EstadoConferenciaUf): ConferenciaUfNota => {
  switch (estado) {
    case 'VALIDADO':
      return 'CONFERIDA'
    case 'SEM_XML':
      return 'RESERVADA_SEM_XML'
    case 'CONFLITO':
      return 'CIENCIA_UF_DIVERGENTE'
  }
}

export const lerNotaFiscalDoXml = (xml: string): NotaFiscalXml | null => {
  const documento = analisarXml<DocumentoNfeXml>(xml)
  const infNFe = documento?.nfeProc?.NFe?.infNFe ?? documento?.NFe?.infNFe
  if (!infNFe || typeof infNFe !== 'object') return null

  const volumes = infNFe.transp?.vol
  const primeiroVolume = Array.isArray(volumes) ? volumes[0] : volumes

  return {
    chaveNfe: textoOuNulo(extractNumbers(textoOuNulo(infNFe['@_Id']) ?? '')),
    ufDestinatario: textoOuNulo(infNFe.dest?.enderDest?.UF),
    razaoSocialEmitente: textoOuNulo(infNFe.emit?.xNome),
    dataEmissao: textoOuNulo(infNFe.ide?.dhEmi),
    volumeCarga: textoOuNulo(primeiroVolume?.qVol),
  }
}

export const lerDestinatarioNotaFiscal = (xml: string): DestinatarioNotaFiscal | null => {
  const nota = lerNotaFiscalDoXml(xml)
  return nota?.chaveNfe && nota.ufDestinatario ? {chaveNfe: nota.chaveNfe, uf: nota.ufDestinatario} : null
}

export const lerNumeroConhecimentoDoXml = (xml: string): number | null => {
  const documento = analisarXml<DocumentoCteXml>(xml)
  const numero = textoOuNulo(documento?.cteProc?.CTe?.infCte?.ide?.nCT ?? documento?.CTe?.infCte?.ide?.nCT)
  if (!numero) return null
  const convertido = Number(numero)
  return Number.isFinite(convertido) ? convertido : null
}

export const lerTextoDoArquivo = (arquivo: Blob): Promise<string | null> =>
  new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = (evento) => {
      const conteudo = evento.target?.result
      resolve(typeof conteudo === 'string' ? conteudo : null)
    }
    reader.onerror = () => resolve(null)
    reader.readAsText(arquivo)
  })

export const lerDestinatarioDoArquivoXml = async (arquivo: Blob): Promise<DestinatarioNotaFiscal | null> => {
  const xml = await lerTextoDoArquivo(arquivo)
  return xml ? lerDestinatarioNotaFiscal(xml) : null
}

export const isArquivoXml = (arquivo: {type?: string; name?: string}) =>
  arquivo.type === 'text/xml' || arquivo.type === 'application/xml' || Boolean(arquivo.name?.toLowerCase().endsWith('.xml'))
